import { useUpdate, useCreate, useNotify, useRefresh } from "ra-core";
import { useCallback, useState } from "react";

interface PromoteLeadArgs {
  contactId: string | number;
  contactName: string;
}

/**
 * Hook: promotes a lead to a customer.
 *
 * 1. Updates contact: contact_type → 'customer', converted_at → now
 * 2. Creates status_change event in the timeline
 * 3. Refreshes the view
 */
export function usePromoteLead() {
  const [update] = useUpdate();
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isPending, setIsPending] = useState(false);

  const promote = useCallback(
    async ({ contactId, contactName }: PromoteLeadArgs) => {
      setIsPending(true);
      try {
        const now = new Date().toISOString();

        // 1. Update contact type
        await update("contacts", {
          id: contactId,
          data: {
            contact_type: "customer",
            converted_at: now,
          },
          previousData: { id: contactId },
        });

        // 2. Create timeline event
        await create("events", {
          data: {
            contact_id: contactId,
            event_type: "status_change",
            payload: {
              from: "lead",
              to: "customer",
              promoted_at: now,
              note: `${contactName} promoted to customer`,
            },
          },
        });

        notify(`${contactName} promoted to customer`, { type: "success" });
        refresh();
      } catch (err) {
        console.error("Promote lead failed:", err);
        notify(`Promotion failed: ${(err as Error).message}`, {
          type: "error",
        });
      } finally {
        setIsPending(false);
      }
    },
    [update, create, notify, refresh]
  );

  return { promote, isPending };
}
